import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const centsToDollars = (c) =>
  (typeof c === "number" ? c : Number(c || 0)) / 100;
const dollarsToCents = (d) =>
  Math.round((typeof d === "number" ? d : Number(d || 0)) * 100);

const defaultForm = (eventId) => ({
  org: undefined,
  event: eventId,
  code: "",
  type: "amount", // "amount" | "percent"
  amountOff: "", // dollars (UI), sent as amountOffCents
  percentOff: "", // number 0-100
  appliesToTicketTypes: [],
  maxRedemptions: "",
  perUserLimit: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
});

function PromoDialog({
  open,
  onClose,
  onSaved,
  eventId,
  initial,
  ticketTypes = [],
  eventObj,
}) {
  const isEdit = Boolean(initial && initial._id);
  const [form, setForm] = useState(defaultForm(eventId));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial && initial._id) {
      setForm({
        org: initial.org,
        event: initial.event || eventId,
        code: initial.code || "",
        type: initial.type || "amount",
        amountOff:
          initial.amountOffCents != null
            ? centsToDollars(initial.amountOffCents)
            : "",
        percentOff:
          initial.percentOff != null ? String(initial.percentOff) : "",
        appliesToTicketTypes: initial.appliesToTicketTypes || [],
        maxRedemptions:
          initial.maxRedemptions != null ? String(initial.maxRedemptions) : "",
        perUserLimit:
          initial.perUserLimit != null ? String(initial.perUserLimit) : "",
        startsAt: (initial.startsAt
          ? new Date(initial.startsAt).toISOString()
          : eventObj?.start || eventObj?.date
          ? new Date(eventObj.start || eventObj.date).toISOString()
          : new Date().toISOString()
        ).slice(0, 16),
        endsAt: (initial.endsAt
          ? new Date(initial.endsAt).toISOString()
          : eventObj?.start || eventObj?.date
          ? new Date(eventObj.start || eventObj.date).toISOString()
          : new Date().toISOString()
        ).slice(0, 16),
        isActive: initial.isActive !== false,
      });
    } else {
      const nowIso = new Date().toISOString();
      const eventEndSource = eventObj?.start || eventObj?.date || null;
      const endIso = eventEndSource
        ? new Date(eventEndSource).toISOString()
        : nowIso;
      setForm({
        ...defaultForm(eventId),
        startsAt: nowIso.slice(0, 16),
        endsAt: endIso.slice(0, 16),
      });
    }
  }, [open, initial, eventId, eventObj]);

  const handleChange = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleToggle = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: Boolean(e.target.checked) }));

  const canSave = useMemo(() => {
    if (!form.code?.trim()) return false;
    if (
      form.type === "amount" &&
      (form.amountOff === "" || isNaN(Number(form.amountOff)))
    )
      return false;
    if (
      form.type === "percent" &&
      (form.percentOff === "" || isNaN(Number(form.percentOff)))
    )
      return false;
    return true;
  }, [form]);

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        event: eventId,
        code: form.code.trim().toUpperCase(),
        type: form.type,
        amountOffCents:
          form.type === "amount"
            ? dollarsToCents(Number(form.amountOff || 0))
            : undefined,
        percentOff:
          form.type === "percent" ? Number(form.percentOff || 0) : undefined,
        appliesToTicketTypes: form.appliesToTicketTypes?.length
          ? form.appliesToTicketTypes
          : undefined,
        maxRedemptions:
          form.maxRedemptions !== "" ? Number(form.maxRedemptions) : undefined,
        perUserLimit:
          form.perUserLimit !== "" ? Number(form.perUserLimit) : undefined,

        startsAt: form.startsAt
          ? new Date(form.startsAt).toISOString()
          : undefined,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
        isActive: Boolean(form.isActive),
      };

      const url = isEdit
        ? `ticketing/discountcode/update/${initial._id}`
        : `ticketing/discountcode/create`;
      const res = await request(url, "post", payload);
      if (!res?.success) {
        toast.error(`Error saving code: ${res.error}`);
      } else {
        toast.success("Code saved successfully");
        onSaved && onSaved(res.data);
        onClose && onClose();
      }
    } catch (err) {
      toast.error(`Code not saved: Error: ${err.message}`);
      // You may have a toast utility; for now just log.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? "Edit promo code" : "Create promo code"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Code"
            value={form.code}
            onChange={handleChange("code")}
            inputProps={{ style: { textTransform: "uppercase" } }}
          />

          <FormControl fullWidth>
            <InputLabel id="promo-type-label">Type</InputLabel>
            <Select
              labelId="promo-type-label"
              label="Type"
              value={form.type}
              onChange={handleChange("type")}
            >
              <MenuItem value="amount">Amount ($ off)</MenuItem>
              <MenuItem value="percent">Percent (% off)</MenuItem>
            </Select>
          </FormControl>

          {form.type === "amount" ? (
            <TextField
              label="Amount off"
              type="number"
              value={form.amountOff}
              onChange={handleChange("amountOff")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          ) : (
            <TextField
              label="Percent off"
              type="number"
              value={form.percentOff}
              onChange={handleChange("percentOff")}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          )}

          <FormControl fullWidth>
            <InputLabel id="applies-to-tt-label">
              Applies to ticket types
            </InputLabel>
            <Select
              multiple
              labelId="applies-to-tt-label"
              label="Applies to ticket types"
              value={form.appliesToTicketTypes || []}
              onChange={(e) => {
                const val = e.target.value || [];
                const ALL = "__ALL__";
                const allIds = (ticketTypes || []).map((t) =>
                  String(t._id || t.id)
                );
                const next = val.includes(ALL)
                  ? allIds
                  : val.filter((v) => v !== ALL);
                setForm((f) => ({ ...f, appliesToTicketTypes: next }));
              }}
              renderValue={(selected) => {
                if (!selected?.length) return "All";
                if (
                  (ticketTypes || []).length &&
                  selected.length === ticketTypes.length
                )
                  return "All";
                const byId = new Map(
                  (ticketTypes || []).map((t) => [
                    String(t._id || t.id),
                    t.name,
                  ])
                );
                return selected
                  .map((id) => byId.get(String(id)) || id)
                  .join(", ");
              }}
            >
              <MenuItem value="__ALL__">
                <em>All</em>
              </MenuItem>
              {(ticketTypes || []).map((tt) => (
                <MenuItem
                  key={String(tt._id || tt.id)}
                  value={String(tt._id || tt.id)}
                >
                  {tt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider flexItem>
            <Typography variant="caption">Limits</Typography>
          </Divider>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Max redemptions (lifetime)"
              type="number"
              value={form.maxRedemptions}
              onChange={handleChange("maxRedemptions")}
              fullWidth
            />
            <TextField
              label="Per-user limit"
              type="number"
              value={form.perUserLimit}
              onChange={handleChange("perUserLimit")}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Starts at"
              type="datetime-local"
              value={form.startsAt}
              onChange={handleChange("startsAt")}
              fullWidth
            />
            <TextField
              label="Ends at"
              type="datetime-local"
              value={form.endsAt}
              onChange={handleChange("endsAt")}
              fullWidth
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={handleToggle("isActive")}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!canSave || saving}
        >
          {isEdit ? "Save changes" : "Create code"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PromoTab = ({ event }) => {
  const eventId = typeof event === "string" ? event : event?._id || event?.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);

  const fetchCodes = async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await request(`ticketing/discountcode?eventId=${eventId}`);
      if (!res?.success)
        throw new Error(res?.error || "Failed to fetch promo codes");
      setRows(res.data || []);
      console.log("[PromoTab] fetched codes", res.data);
    } catch (err) {
      console.error("[PromoTab] fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  const getTicketTypes = async () => {
    const result = await request(
      `ticketing/tickettype/event?eventId=${eventId}`
    );
    if (result.success) {
      setTicketTypes(result.data?.ticketTypes);
    }
  };

  useEffect(() => {
    fetchCodes();
    getTicketTypes();
  }, [eventId]);

  const openMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setActiveRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveRow(null);
  };

  const onCreate = () => {
    setDialogInitial(null);
    setDialogOpen(true);
  };
  const onEdit = () => {
    setDialogInitial(activeRow);
    setDialogOpen(true);
    closeMenu();
  };

  const onDelete = async () => {
    try {
      if (!activeRow?._id) return;
      const res = await request(
        `ticketing/discountcode/delete/${activeRow._id}`,
        "post",
        {}
      );
      if (!res?.success) {
        toast.error(`Error deleting code. Error: ${res.error}`);
      } else {
        toast.success("Code deleted successfully");
        closeMenu();
        fetchCodes();
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const onSaved = () => {
    fetchCodes();
  };

  const ValueCell = ({ row }) => {
    if (row.type === "percent") return <>{row.percentOff}%</>;
    const dollars =
      row.amountOffCents != null ? centsToDollars(row.amountOffCents) : 0;
    return <>${dollars.toFixed(2)}</>;
  };

  const UsesLimitsCell = ({ row }) => {
    const used = row.redemptionsCount || 0;
    const max = row.maxRedemptions ?? "∞";
    return (
      <Typography
        variant="body2"
        style={{ marginLeft: 10 }}
      >{`${used}/${max}`}</Typography>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Promo Codes</Typography>
        <Button onClick={onCreate} variant="contained" startIcon={<AddIcon />}>
          Create code
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Uses / Limits</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((row) => (
              <TableRow
                key={row._id}
                hover
                onClick={() => {
                  setDialogInitial(row);
                  setDialogOpen(true);
                }}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={700}>{row.code}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <ValueCell row={row} />
                </TableCell>
                <TableCell>
                  <UsesLimitsCell row={row} />
                </TableCell>
                <TableCell>
                  {row.isActive ? (
                    <Chip size="small" color="success" label="Active" />
                  ) : (
                    <Chip size="small" label="Inactive" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => openMenu(e, row)}
                    aria-label="More"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!rows || rows.length === 0) && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No promo codes yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem onClick={onEdit}>Edit</MenuItem>
        <MenuItem onClick={onDelete}>Delete</MenuItem>
      </Menu>

      <PromoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={onSaved}
        eventId={eventId}
        initial={dialogInitial}
        ticketTypes={ticketTypes}
        eventObj={event}
      />
    </Box>
  );
};

export default PromoTab;
